import type {
  EstimateDetailResponse,
  EstimateShareValue,
  PublicBuildingAnalysis,
  PublicLandAnalysis,
} from '@popety-io/types'
import { apiClient } from './apiClient'
import { getBuildingShareSavedItems } from './buildingShareSavedItems'

class PublicApiService {
  /**
   * Get building analysis by hash using the new building-analysis-public-share endpoint
   * Returns structured data with only the sections that were shared
   */
  async getBuildingAnalysis(hash: string): Promise<PublicBuildingAnalysis> {
    const { data } = await apiClient.get(
      `/building-analysis-public-share/public/${hash}`
    )

    // Parse sections - can be a string, array, or object
    let sections: string[] = []
    if (typeof data.sections === 'string') {
      try {
        sections = JSON.parse(data.sections)
      } catch (e) {
        console.error('Failed to parse sections:', e)
        sections = []
      }
    } else if (Array.isArray(data.sections)) {
      sections = data.sections
    } else if (typeof data.sections === 'object' && data.sections !== null) {
      // Handle object format { "transport": true, "summary": true, ... }
      sections = Object.keys(data.sections).filter((key) => data.sections[key])
    }

    // Transform the new structure to match the expected PublicBuildingAnalysis interface
    // The value object contains section-keyed data
    // ALWAYS use header data if available (for essential header information)
    // Otherwise fallback to summary data for backward compatibility
    const header = data.value.header || {}
    const summary = data.value.summary || {}

    const transformedData: PublicBuildingAnalysis = {
      // ALWAYS spread header data first (contains essential info for PropertyHeader)
      ...header,

      // Then spread summary data for backward compatibility
      ...summary,

      // Spread all other section data for tabs to access (e.g., buildingData.energy, buildingData.noise)
      ...data.value,

      // Override summary to keep it as a section
      summary: data.value.summary,
      header: data.value.header,

      // Ensure building object is properly structured (header.building takes precedence)
      building:
        data.value.header?.building ||
        data.value.building ||
        summary.building ||
        {},

      // Map section IDs to component expected names
      buildingCharacteristics: data.value.characteristics,
      unitDetails: data.value.units,

      // Market data mapping (orchestrator saves separately, component expects marketAggregations)
      marketAggregations: {
        purchase: data.value.purchase_market?.purchase || null,
        rental: data.value.rental_market?.rent || null,
        // Include default filters used for the market analysis
        defaultFilters:
          data.value.purchase_market?.defaultFilters ||
          data.value.rental_market?.defaultFilters ||
          null,
      },

      // Transport section - complete transport & access data
      transport: data.value.transport || null,

      // Other environment sections
      amenities: data.value.amenities || null,
      noise: data.value.noise || null,
      sunlight: data.value.sunlight || null,

      // Social & economic sections
      demographics: data.value.demographics || null,
      employment: data.value.employment || null,

      // Buildings administrative structure for unit details processing
      // This is the complete nested structure needed by processBuildingData
      buildings_administrative:
        data.value.units?.buildings_administrative ||
        summary.buildings_administrative ||
        [],

      // Unit statistics - prefer calculated values from units section over summary
      unit_nb: data.value.units?.unit_nb ?? summary.unit_nb,
      dwellings_nb: data.value.units?.dwellings_nb ?? summary.dwellings_nb,
      dwelling_median_room_nb:
        data.value.units?.dwelling_median_room_nb ??
        summary.dwelling_median_room_nb,
      dwelling_median_area:
        data.value.units?.dwelling_median_area ?? summary.dwelling_median_area,
      dwellings_total_area:
        data.value.units?.dwellings_total_area ?? summary.dwellings_total_area,
      number_of_separate_habitable_rooms:
        data.value.units?.number_of_separate_habitable_rooms ??
        summary.number_of_separate_habitable_rooms,

      // Units and floors from various possible sources
      // The units section from orchestrator has dwellings array
      // Use nullish coalescing to properly handle empty arrays
      units: data.value.units?.dwellings ?? [],
      floors: data.value.floors || summary.floors || [],

      // Normalize section arrays and legacy saved-item aliases for shared tabs.
      ...getBuildingShareSavedItems(data.value),

      // Metadata
      hash: data.hash,
      user: data.user,
      company: data.company,
      office: data.office,
      creation_date: data.create_timestamp,

      // Store available sections for filtering
      available_sections: sections,
    }

    return transformedData
  }

  /**
   * Get land analysis by hash using the new land-analysis-public-share endpoint
   * Returns structured data with only the sections that were shared
   */
  async getLandAnalysis(hash: string): Promise<PublicLandAnalysis> {
    const { data } = await apiClient.get(
      `/land-analysis-public-share/public/${hash}`
    )

    // Transform the new structure to match the expected PublicLandAnalysis interface
    // The value object contains section-keyed data (e.g., { summary: {...}, history: {...} })
    const summary = data.value.summary || {}

    const transformedData: PublicLandAnalysis = {
      // Spread all section data from the summary
      ...summary,

      // Extract scores from summary.scores object
      development_score: summary.scores?.development_score,
      under_exploited_score: summary.scores?.under_exploited_score,
      livability_score: summary.scores?.livability_score,
      existing_score: summary.scores?.existing_score,
      geo_shape_score: summary.scores?.geo_shape_score,

      // Add additional section data
      history: data.value.history,
      geoAnalysis: data.value.geo,
      buildings:
        data.value.buildings?.buildings ||
        data.value.restrictions?.buildings ||
        summary.buildings ||
        [],
      zoning: data.value.zoning,
      restrictions: data.value.restrictions,
      // RDPPF restrictions data - prefer summary, fall back to restrictions section
      rdppfs: summary.rdppfs || data.value.restrictions?.rdppfs || [],
      rdppf_land_link:
        summary.rdppf_land_link ||
        data.value.restrictions?.rdppf_land_link ||
        null,
      // Region restriction types (all types including non-affecting)
      regionRestrictions: data.value.restrictions?.regionRestrictions || [],
      purchase_market: data.value.purchase_market?.purchase,
      rental_market: data.value.rental_market?.rent,

      // Market data mapping (component expects marketAggregations structure)
      marketAggregations: {
        purchase: data.value.purchase_market?.purchase || null,
        rental: data.value.rental_market?.rent || null,
        // Include default filters used for the market analysis
        defaultFilters:
          data.value.purchase_market?.defaultFilters ||
          data.value.rental_market?.defaultFilters ||
          null,
      },

      transport: data.value.transport,
      access: data.value.access || data.value.transport,
      amenities: data.value.amenities,
      noise: data.value.noise,
      sunlight: data.value.sunlight,
      demographics: data.value.demographics,
      employment: data.value.employment,
      taxes: data.value.taxes,
      electricity: data.value.electricity,
      feasibility: data.value.feasibility,
      construction_permits: data.value.construction_permits,
      transactions: data.value.transactions,
      // Aliases for shared tab components (they look for saved_* fields)
      // Section data may be placeholder objects (not arrays), so check with Array.isArray
      saved_transactions:
        (Array.isArray(data.value.saved_transactions)
          ? data.value.saved_transactions
          : null) ||
        (Array.isArray(data.value.transactions)
          ? data.value.transactions
          : null) ||
        (Array.isArray(summary.transactions) ? summary.transactions : null) ||
        [],
      saved_building_permits:
        (Array.isArray(data.value.saved_building_permits)
          ? data.value.saved_building_permits
          : null) ||
        (Array.isArray(data.value.construction_permits)
          ? data.value.construction_permits
          : null) ||
        (Array.isArray(summary.construction_permits)
          ? summary.construction_permits
          : null) ||
        [],
      purchase_listings: Array.isArray(data.value.purchase_listings)
        ? data.value.purchase_listings
        : Array.isArray(summary.purchase_listings)
          ? summary.purchase_listings
          : [],
      rental_listings: Array.isArray(data.value.rental_listings)
        ? data.value.rental_listings
        : Array.isArray(summary.rental_listings)
          ? summary.rental_listings
          : [],

      // Add metadata from response
      hash: data.hash,
      user: data.user,
      company: data.company,
      office: data.office,
      creation_date: data.create_timestamp,

      // Store available sections for filtering
      available_sections: data.section,
      notes: Array.isArray(data.value.notes) ? data.value.notes : undefined,
      files: Array.isArray(data.value.files) ? data.value.files : undefined,
    }

    return transformedData
  }

  /**
   * Get land grouping analysis by hash.
   * Reuses the section-keyed value structure from land-analysis so the same
   * UI components can render grouping data.
   */
  async getLandGroupingAnalysis(hash: string): Promise<PublicLandAnalysis> {
    const { data } = await apiClient.get(
      `/land-grouping-public-share/public/${hash}`
    )

    const value = data.value || {}
    const summary = value.summary || {}

    const transformedData: PublicLandAnalysis = {
      ...summary,

      development_score: summary.scores?.development_score,
      under_exploited_score: summary.scores?.under_exploited_score,
      livability_score: summary.scores?.livability_score,
      existing_score: summary.scores?.existing_score,
      geo_shape_score: summary.scores?.geo_shape_score,

      history: value.history,
      geoAnalysis: value.geo,
      buildings:
        value.buildings?.buildings ||
        value.restrictions?.buildings ||
        summary.buildings ||
        [],
      zoning: value.zoning,
      restrictions: value.restrictions,
      rdppfs: summary.rdppfs || value.restrictions?.rdppfs || [],
      rdppf_land_link:
        summary.rdppf_land_link || value.restrictions?.rdppf_land_link || null,
      regionRestrictions: value.restrictions?.regionRestrictions || [],
      purchase_market: value.purchase_market?.purchase,
      rental_market: value.rental_market?.rent,

      marketAggregations: {
        purchase: value.purchase_market?.purchase || null,
        rental: value.rental_market?.rent || null,
        defaultFilters:
          value.purchase_market?.defaultFilters ||
          value.rental_market?.defaultFilters ||
          null,
      },

      transport: value.transport,
      access: value.access || value.transport,
      amenities: value.amenities,
      noise: value.noise,
      sunlight: value.sunlight,
      demographics: value.demographics,
      employment: value.employment,
      taxes: value.taxes,
      electricity: value.electricity,
      feasibility: value.feasibility,
      construction_permits: value.construction_permits,
      transactions: value.transactions,
      saved_transactions: Array.isArray(value.transactions)
        ? value.transactions
        : [],
      saved_building_permits: Array.isArray(value.construction_permits)
        ? value.construction_permits
        : [],
      purchase_listings: Array.isArray(value.purchase_listings)
        ? value.purchase_listings
        : [],
      rental_listings: Array.isArray(value.rental_listings)
        ? value.rental_listings
        : [],

      hash: data.hash,
      user: data.user,
      company: data.company,
      office: data.office,
      creation_date: data.create_timestamp || data.creation_date,
      available_sections: data.section,
      notes: Array.isArray(value.notes) ? value.notes : undefined,
      files: Array.isArray(value.files) ? value.files : undefined,
    }

    return transformedData
  }

  /**
   * Get estimate by hash
   * First tries the new estimate-public-shares endpoint (with section filtering)
   * Falls back to legacy /public/estimate endpoint for backward compatibility
   */
  async getEstimateByHash(hash: string): Promise<{
    data: EstimateDetailResponse & {
      available_sections?: string[]
      notes?: EstimateShareValue['notes']
      files?: EstimateShareValue['files']
    }
  }> {
    try {
      // Try new estimate-public-shares endpoint first
      const { data } = await apiClient.get(
        `/estimate-public-shares/public/${hash}`
      )

      // Parse sections - can be a string or array
      let sections: string[] = []
      if (typeof data.sections === 'string') {
        try {
          sections = JSON.parse(data.sections)
        } catch (e) {
          console.error('Failed to parse sections:', e)
          sections = []
        }
      } else if (Array.isArray(data.sections)) {
        sections = data.sections
      }

      // The public mapper puts the legacy property blob on `value`. Notes and
      // files are siblings on the raw stored blob (`share_value`).
      const attachmentBlob = data.share_value ?? data.value
      const transformedData = {
        ...data,
        value: data.value,
        value_edited: data.value_edited || data.value,
        available_sections: sections,
        notes: Array.isArray(attachmentBlob?.notes)
          ? attachmentBlob.notes
          : undefined,
        files: Array.isArray(attachmentBlob?.files)
          ? attachmentBlob.files
          : undefined,
      }

      return { data: transformedData }
    } catch (error: any) {
      // If new endpoint returns 404 (hash not found in new table),
      // fall back to legacy endpoint
      if (error?.response?.status === 404) {
        const result = await apiClient.get(`/public/estimate/${hash}`)
        // Legacy endpoint doesn't have section filtering - all sections available
        return {
          data: {
            ...result.data,
            available_sections: undefined, // undefined means all sections are available
          },
        }
      }
      throw error
    }
  }

  // Validate public link access
  async validateAccess(
    publicId: string
  ): Promise<{ isValid: boolean; expiresAt?: string }> {
    return apiClient.get(`/public/validate/${publicId}`)
  }
}

export const publicApiService = new PublicApiService()
export default publicApiService
